import { useParams, Link } from "react-router-dom";
import { ArrowUpRight, CheckCircle2, AlertCircle, ArrowRight } from "lucide-react";
import { Seo, breadcrumbLd, faqLd, PROVIDER } from "@/components/site/Seo";
import { PageHero } from "@/components/site/PageHero";
import { Reveal } from "@/components/site/Reveal";
import { SectionHead } from "@/components/site/SectionHead";
import { FaqList } from "@/components/site/FaqList";
import { StepForm } from "@/components/site/StepForm";
import { CaseCard } from "@/components/site/CaseCard";
import { CtaBand } from "@/components/site/CtaBand";
import { BlogLinks } from "@/components/site/BlogLinks";
import { PsychologyCro, PPC_LIVE_VIDEO } from "@/components/site/PsychologyCro";
import { BookCallButton } from "@/components/site/Ctas";
import { ServiceIcon } from "@/components/site/ServiceIcon";
import { MetaWhy, MetaSteps, MetaObjectives, MetaWhyUs, MetaFinalCta } from "@/components/site/MetaAdsSections";
import { META_PRICING } from "@/data/metaAds";
import { useContent, useItem, SITE_URL } from "@/lib/api";
import NotFound from "./NotFound";

const list = (v) => (Array.isArray(v) ? v : []);

const CHANNEL_MATCH = {
  "google-ads-management": "Google Ads", "facebook-instagram-ads": "Facebook & Instagram Ads", seo: "SEO", "website-design-development": "Website Design & Development",
};

const Problems = ({ svc }) => (
  <section className="bg-white py-24 sm:py-28">
    <div className="mx-auto grid max-w-7xl gap-12 px-5 sm:px-8 lg:grid-cols-2">
      <div>
        <SectionHead eyebrow="The problems it solves" title="Sound familiar?" />
        <ul className="mt-10 space-y-3">
          {list(svc.problems).map((p, i) => (
            <Reveal key={p} delay={i * 0.05}>
              <li className="flex items-start gap-3 rounded-2xl border border-violet-100 bg-[#FBFAFF] p-4 text-base font-semibold text-[#13052A]">
                <AlertCircle className="mt-0.5 h-5 w-5 shrink-0 text-[#7C3AED]" /> {p}
              </li>
            </Reveal>
          ))}
        </ul>
      </div>
      <div className="lg:pt-24">
        <Reveal className="rounded-[28px] bg-[#13052A] p-8 text-white sm:p-10" data-testid="service-who-for">
          {svc.suitable_title
            ? <h2 className="text-2xl font-extrabold leading-tight sm:text-3xl" data-testid="who-for-title">{svc.suitable_title}</h2>
            : <p className="eyebrow text-violet-300">Who it's for</p>}
          {list(svc.suitable_intro).map((p) => <p key={p} className="mt-4 text-base leading-relaxed text-violet-100">{p}</p>)}
          {svc.suitable_lead && <p className="mt-6 text-base font-bold text-white">{svc.suitable_lead}</p>}
          <ul className="mt-6 space-y-4">
            {list(svc.suitable_for).map((p) => (
              <li key={p} className="flex items-start gap-3 text-base text-violet-50"><CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-[#60A5FA]" />{p}</li>
            ))}
          </ul>
          {list(svc.suitable_outro).length > 0 && (
            <div className="mt-6 space-y-4 border-t border-white/15 pt-6 text-base leading-relaxed text-violet-100" data-testid="who-for-outro">
              {list(svc.suitable_outro).map((p) => <p key={p}>{p}</p>)}
            </div>
          )}
        </Reveal>
      </div>
    </div>
  </section>
);

const Deliverables = ({ svc }) => (
  <section id={svc.slug === "analytics-tracking-cro" ? "cro" : undefined} className="scroll-mt-24 bg-[#F5F0FF] py-24 sm:py-28">
    <div className="mx-auto max-w-7xl px-5 sm:px-8">
      <SectionHead eyebrow="What's included" title="What the service involves" />
      {list(svc.capabilities).length > 0 ? (
        <Reveal className="mt-12 rounded-[28px] bg-white p-7 sm:p-10">
          <ul className="grid gap-x-8 gap-y-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="service-capabilities">
            {list(svc.capabilities).map((c) => (
              <li key={c} className="flex items-start gap-3 text-base font-semibold text-[#13052A]"><CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-[#7C3AED]" />{c}</li>
            ))}
          </ul>
        </Reveal>
      ) : (
      <div className="mt-12 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {list(svc.deliverables).map((d, i) => (
          <Reveal key={d.title} delay={(i % 4) * 0.06} className={`h-full ${list(svc.deliverables).length % 4 === 2 && i >= list(svc.deliverables).length - 2 ? "lg:col-span-2" : ""}`}>
            <div className="h-full rounded-[24px] bg-white p-6 transition-transform duration-500 hover:-translate-y-1">
              <span className="text-xs font-bold text-[#7C3AED]">{String(i + 1).padStart(2, "0")}</span>
              <h3 className="mt-3 text-lg font-extrabold text-[#13052A]">{d.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-slate-600">{d.text}</p>
            </div>
          </Reveal>
        ))}
      </div>
      )}
    </div>
  </section>
);

const MORE_LINKS = {
  "website-design-development": [["/website-hosting", "Website Hosting", "Reliable, affordable hosting from £25 per month."], ["/domain-registration", "Domain Registration & Management", "Find, register and manage the right online address."]],
  seo: [["/seo-consultant-tunbridge-wells", "SEO Consultant Tunbridge Wells", "Local SEO consultancy for businesses in Tunbridge Wells."], ["/seo-consultant-kent", "SEO Consultant Kent", "SEO for businesses across Kent, including Tonbridge, Sevenoaks and Crowborough."], ["/seo-backlinks-link-building", "SEO Backlinks & Link Building", "Build authority with ethical, relevant, high-quality backlinks."], ["/local-seo-tunbridge-wells", "Local SEO Tunbridge Wells", "Google Maps visibility, reviews and local pages."]],
  "google-ads-management": [["/google-ads-tunbridge-wells", "Google Ads Tunbridge Wells", "Local Google Ads campaigns for Tunbridge Wells businesses."], ["/ppc-consultant-tunbridge-wells", "PPC Consultant Tunbridge Wells", "Paid search and paid social, planned together."], ["/google-ads-sevenoaks", "Google Ads Sevenoaks", "Google Ads for high-value services in Sevenoaks."], ["/analytics-tracking-cro", "Analytics, Tracking & CRO", "Measure every enquiry and turn more clicks into customers."]],
  "facebook-instagram-ads": [["/facebook-ads-tunbridge-wells", "Facebook Ads Tunbridge Wells", "Meta Ads for Tunbridge Wells businesses."], ["/meta-ads-kent", "Meta Ads Kent", "Facebook and Instagram Ads for businesses across Kent."], ["/ecommerce-facebook-ads", "E-commerce Facebook Ads", "Meta Ads for online shops and brands."], ["/gym-marketing", "Gym Marketing", "Advertising that turns enquiries into paying members."]],
  "google-business-profile": [["/local-seo-tunbridge-wells", "Local SEO Tunbridge Wells", "The wider local SEO picture: maps, reviews, listings and local pages."], ["/seo-consultant-crowborough", "SEO Consultant Crowborough", "Local SEO for Crowborough and Wealden businesses."]],
};

const MoreLinks = ({ slug }) => (
  <section className="bg-[#F5F0FF] pb-24 sm:pb-28" data-testid="service-more-links">
    <div className="mx-auto grid max-w-7xl gap-4 px-5 sm:grid-cols-2 sm:px-8">
      {MORE_LINKS[slug].map(([to, title, text]) => (
        <Reveal key={to}>
          <Link to={to} className="group flex items-center justify-between gap-4 rounded-[24px] border border-violet-200 bg-white p-6 transition-[transform,box-shadow] duration-500 hover:-translate-y-1 hover:shadow-[0_30px_60px_-30px_rgba(46,16,101,.35)]" data-testid={`service-more-link${to.replace(/\//g, "-")}`}>
            <span>
              <span className="block text-lg font-extrabold text-[#13052A]">{title}</span>
              <span className="mt-1 block text-sm text-slate-600">{text}</span>
            </span>
            <ArrowUpRight className="h-5 w-5 shrink-0 text-[#7C3AED] transition-transform duration-500 group-hover:-translate-y-1" />
          </Link>
        </Reveal>
      ))}
    </div>
  </section>
);

const Approach = ({ svc }) => (
  <section className="grain relative overflow-hidden bg-[#13052A] py-24 text-white sm:py-28">
    <div className="relative mx-auto max-w-7xl px-5 sm:px-8">
      <SectionHead dark eyebrow={svc.slug === "website-design-development" ? "My process" : "Freya's approach"} title={svc.slug === "website-design-development" ? "How your new website comes together, step by step" : "How I'll work on it with you"} />
      <ol className="mt-14 grid gap-6 md:grid-cols-2 lg:grid-cols-4">
        {list(svc.approach).map((a, i) => (
          <Reveal key={a.title} delay={i * 0.08}>
            <li className="relative border-t border-white/15 pt-6">
              <span className="serif-accent text-5xl text-[#C4B5FD]">{i + 1}</span>
              <h3 className="mt-3 text-lg font-extrabold">{a.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-violet-100">{a.text}</p>
              {list(a.points).length > 0 && (
                <ul className="mt-4 space-y-2">
                  {list(a.points).map((pt) => (
                    <li key={pt} className="flex items-start gap-2 text-sm leading-snug text-violet-50">
                      <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-[#C4B5FD]" /> {pt}
                    </li>
                  ))}
                </ul>
              )}
            </li>
          </Reveal>
        ))}
      </ol>
    </div>
  </section>
);

export default function ServicePage({ slugOverride }) {
  const { slug: p } = useParams();
  const slug = slugOverride || p;
  const { item: svc, loading, error } = useItem("services", slug);
  const { items: cases } = useContent("case_studies");
  const { items: pricing } = useContent("pricing");
  if (error) return <NotFound />;
  if (loading || !svc) return <div className="min-h-screen bg-[#13052A]" />;

  const crumbs = [["Home", "/"], [svc.nav_title, `/${svc.slug}`]];
  const price = pricing.find((x) => x.service_slug === svc.slug && x.primary) || pricing.find((x) => x.service_slug === svc.slug);
  const related = cases.filter((c) => list(c.channels).includes(CHANNEL_MATCH[svc.slug])).slice(0, 3);
  const meta = svc.slug === "facebook-instagram-ads";

  return (
    <>
      <Seo title={svc.meta_title} description={svc.meta_description} path={`/${svc.slug}`}
        jsonLd={[{ "@type": "Service", name: svc.nav_title, serviceType: svc.nav_title, description: svc.meta_description, provider: PROVIDER, url: `${SITE_URL}/${svc.slug}`,
          areaServed: ["Kent", "East Sussex", "West Sussex", "Surrey", "London", "United Kingdom"] }, breadcrumbLd(crumbs), faqLd(list(svc.faqs)), ...(svc.slug === "analytics-tracking-cro" ? [PPC_LIVE_VIDEO] : [])]} />
      <PageHero crumbs={crumbs} eyebrow={svc.eyebrow} lines={[svc.h1]} intro={svc.intro}
        aside={
          <div className="rounded-[28px] border border-white/10 bg-white/5 p-7 backdrop-blur-xl">
            <span className="flex h-14 w-14 items-center justify-center rounded-2xl bg-[#7C3AED] text-white"><ServiceIcon name={svc.icon} className="h-6 w-6" /></span>
            <p className="mt-6 text-lg font-bold text-white">{svc.short}</p>
            {price && <p className="mt-4 text-sm text-violet-200" data-testid="service-price-note">{price.fee_type}: <span className="font-bold text-white">{price.price}</span></p>}
            <p className="mt-2 text-xs text-violet-200">{svc.price_note}</p>
          </div>
        }>
        <a href="#enquire" className="btn-primary" data-testid="service-hero-audit">Get a Free Digital Audit <ArrowUpRight className="h-4 w-4" /></a>
        <BookCallButton testid="service-hero-book-call" />
      </PageHero>
      {meta && <MetaWhy />}
      <Problems svc={svc} />
      {svc.slug === "analytics-tracking-cro" && <PsychologyCro />}
      {meta ? <MetaSteps /> : <Deliverables svc={svc} />}
      {MORE_LINKS[svc.slug] && <MoreLinks slug={svc.slug} />}
      {meta && <MetaObjectives />}
      <BlogLinks page={svc.slug} />
      {meta ? <MetaWhyUs /> : <Approach svc={svc} />}

      <section className="bg-white py-24 sm:py-28">
        <div className="mx-auto grid max-w-7xl gap-6 px-5 sm:px-8 lg:grid-cols-2">
          <Reveal className="rounded-[28px] border border-violet-200 p-8 sm:p-10">
            <p className="eyebrow text-[#7C3AED]">Pricing</p>
            {meta && <p className="mt-4 text-base font-bold text-[#13052A]">Facebook & Instagram Ads Management</p>}
            <h2 className={`${meta ? "mt-1" : "mt-4"} text-2xl font-extrabold text-[#13052A] sm:text-3xl`} data-testid="service-price">{price ? price.price : "Quoted to your needs"}</h2>
            {price && <p className="mt-2 text-sm font-semibold text-slate-600">{price.fee_type}{price.setup ? ` · ${price.setup}` : ""}</p>}
            {meta
              ? <div className="mt-4 space-y-3 text-base leading-relaxed text-slate-600" data-testid="meta-pricing-copy">{META_PRICING.map((t) => <p key={t}>{t}</p>)}</div>
              : <p className="mt-4 text-base leading-relaxed text-slate-600">{svc.price_note}</p>}
            <Link to="/pricing" className="btn-ghost mt-7" data-testid="service-pricing-link">View all pricing <ArrowRight className="h-4 w-4" /></Link>
          </Reveal>
          <Reveal delay={0.08} className="rounded-[28px] bg-[#F5F0FF] p-8 sm:p-10">
            <p className="eyebrow text-[#7C3AED]">Results</p>
            <h2 className="mt-4 text-2xl font-extrabold text-[#13052A] sm:text-3xl">Relevant client work</h2>
            <p className="mt-4 text-base leading-relaxed text-slate-600">Verified results are published with each client's approval. See the full case study library for campaign strategy and outcomes.</p>
            <Link to="/results" className="btn-ghost mt-7" data-testid="service-results-link">Results & case studies <ArrowRight className="h-4 w-4" /></Link>
          </Reveal>
        </div>
        {related.length > 0 && (
          <div className="mx-auto mt-6 grid max-w-7xl gap-5 px-5 sm:px-8 md:grid-cols-2 lg:grid-cols-3">
            {related.map((c, i) => <Reveal key={c.slug} delay={i * 0.06} className="h-full"><CaseCard c={c} i={i} /></Reveal>)}
          </div>
        )}
      </section>

      <section className="bg-[#F5F0FF] py-24 sm:py-28">
        <div className="mx-auto grid max-w-7xl gap-12 px-5 sm:px-8 lg:grid-cols-[1fr_1.4fr]">
          <SectionHead eyebrow="FAQs" title={`${svc.nav_title}: your questions answered`} />
          <Reveal><FaqList faqs={list(svc.faqs)} /></Reveal>
        </div>
      </section>

      <section id="enquire" className="scroll-mt-20 bg-gradient-to-br from-[#581C87] via-[#4C1D95] to-[#1E3A8A] py-24 sm:py-28" data-testid="service-enquiry-section">
        <div className="mx-auto grid max-w-7xl gap-12 px-5 sm:px-8 lg:grid-cols-[1fr_1.2fr]">
          <SectionHead dark eyebrow="Start here" title="Request your free digital audit" text={`Tell me about your business and I'll review your ${svc.nav_title.toLowerCase()} opportunities personally.`} />
          <Reveal><StepForm formType={`service:${svc.slug}`.slice(0, 40)} defaultServices={[svc.form_service === "Website Design & Development" ? "Website Design" : svc.form_service]} /></Reveal>
        </div>
      </section>
      {meta ? <MetaFinalCta /> : <CtaBand />}
    </>
  );
}
